import * as React from 'react';
import styled, { keyframes, css } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import type { Message, ExternalSources } from './types';
import { MessageBubble, MessageInput, Thinking, FadeUpAnimation } from './components';
import { SourcesPanel } from './components/SourcesPanel';
import { AllSourcesPanel } from './components/AllSourcesPanel';
import { Button, Spinner, Typography } from '@mindlercare/ui-web';
import { Icon, Illustration } from '@mindlercare/ui-web';
import { defaultTheme } from '@mindlercare/ui-web/src/shared/constants';
import { Translation, Translations, Locale } from './translations';
import { TranslationsContext, useTranslation } from './translations/TranslationHook';

export interface ChatProps {
  /** List of messages that have been sent */
  messages: Message[];

  /** Whether the agent is currently replying */
  isAgentReplying?: boolean;

  /** Current state of the chat - INITIATING shows loading, READY enables interaction
   * 
   * For example INITIATING state can be set if it takes time to create a new session or load
   * in grounding resources
  */
  state: "INITIATING" | "DISABLED" | "READY";

  /** Current value of the message input field */
  input: string;

  /** Whether to allow pasting text into the input field */
  allowPasting?: boolean;

  /** Callback fired when the input value changes */
  onInputChange: (value: string) => void;

  /** Callback fired when the send button is clicked or Enter is pressed */
  onSend: () => void;

  /** Optional sources mapping for opening external URLs when clicking citations */
  sources?: ExternalSources;

  /** Optional session ID to display in the header */
  sessionId?: string;

  /** Locale for translations (defaults to 'en') */
  locale?: string;

  /** Override specific translation strings */
  translations?: Partial<Translation>;

  /** Chat mode - 'interactive' allows user input and actions, 'readonly' is for viewing messages only */
  mode?: "interactive" | "readonly";

  /** Optional information message to display above the input field */
  informationMessage?: {
    text: string;
    severity: "info" | "warning";
  };

  /** Callback fired when the information message close button is clicked */
  onInformationMessageClose?: () => void;

  /** Callback fired when thumbs up/down is clicked. Pass true for thumbs up, false for thumbs down, undefined to clear */
  onThumbsFeedback?: (messageId: string, feedback: boolean | undefined) => void;

  /** Map of message IDs to their thumbs feedback state */
  messageFeedback?: Record<string, boolean | undefined>;

  /** Whether to show thumbs up/down feedback buttons (defaults to false) */
  showFeedback?: boolean;
}

const Root = styled.div({
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  background: defaultTheme.COLORS.WHITE,
  color: defaultTheme.COLORS.GRAY_BLACK,
  fontFamily: defaultTheme.FONT.FAMILY.BODY,
  position: 'relative',
  border: 'none',
  overflow: 'hidden',

  '& p': {
    margin: `${defaultTheme.SPACING.SM} 0`,
  },
});

const RightSpacer = styled.div<{ $isExpanded: boolean }>(({ $isExpanded }) => ({
  width: $isExpanded ? '326px' : '0px',
  flexShrink: 0,
  position: 'relative',
  transition: 'width 0.3s ease-in-out',
  overflow: 'hidden',
  paddingBottom: defaultTheme.SPACING.MD,
}));

const ChatContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  height: '100%',
  overflow: 'hidden',
  minWidth: 0,
});

const FadeInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${defaultTheme.SPACING.SM} ${defaultTheme.SPACING.MD};
  border-bottom: 1px solid ${defaultTheme.COLORS.GRAY_LIGHT};
  background: ${defaultTheme.COLORS.WHITE};
  min-height: 48px;
  ${css`
    animation: ${FadeInFromTop} 0.3s ease-out;
  `}
`;

const SessionIdContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: defaultTheme.SPACING.XS,
  fontSize: defaultTheme.FONT.SIZE.SMALL,
  color: defaultTheme.COLORS.GRAY_DARK,
});

const SessionIdLabel = styled.span({
  fontWeight: 500,
  fontSize: defaultTheme.FONT.SIZE.SMALL,
  color: defaultTheme.COLORS.GRAY_BLACK,
});

const SessionIdValue = styled.span({
  fontFamily: 'monospace',
  fontSize: defaultTheme.FONT.SIZE.SMALL,
  padding: `${defaultTheme.SPACING.XXS} ${defaultTheme.SPACING.XS}`,
  background: defaultTheme.COLORS.GRAY_X_LIGHT,
  borderRadius: defaultTheme.BORDER_RADIUS.SM,
  color: defaultTheme.COLORS.GRAY_BLACK,
});

const MessagesContainer = styled.div({
  flex: 1,
  overflowY: 'auto',
  padding: defaultTheme.SPACING.MD,
  display: 'flex',
  flexDirection: 'column',
  gap: defaultTheme.SPACING.SM,
  scrollBehavior: 'smooth',
  position: 'relative',
  paddingBottom: defaultTheme.SPACING.XL,

  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: defaultTheme.COLORS.GRAY_LIGHT,
    borderRadius: defaultTheme.BORDER_RADIUS.SM,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: defaultTheme.COLORS.GRAY_MEDIUM,
  },
});

const StreamContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: defaultTheme.SPACING.LG,
});

const StyledMessageRow = styled.div<{ $isUser?: boolean }>(({ $isUser }) => ({
  display: 'flex',
  gap: defaultTheme.SPACING.SM,
  alignItems: 'flex-start',
  width: '100%',
  justifyContent: $isUser ? 'flex-end' : 'flex-start',
}));

const AssistantMessageRow = styled(StyledMessageRow)({
  alignItems: 'baseline',
});

const Avatar = styled.div({
  width: '32px',
  height: '32px',
  marginTop: 0,
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  borderRadius: '100%',
  transform: 'translateY(4px)',
  background: defaultTheme.COLORS.PRIMARY_LIGHT,
});

const EmptyStateContainer = styled.div({
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  pointerEvents: 'none',
});

const EmptyStateContent = styled.div({
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: defaultTheme.SPACING.LG,
});

const InputWrapper = styled.div({
  padding: `${defaultTheme.SPACING.MD} ${defaultTheme.SPACING.MD} ${defaultTheme.SPACING.MD}`,
  background: defaultTheme.COLORS.WHITE,
  display: 'flex',
  justifyContent: 'center',
});

const InputContainer = styled.div({
  width: '100%',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 2px 8px rgba(0,0,0,0.08)',
  borderRadius: defaultTheme.BORDER_RADIUS.MD,
  overflow: 'hidden',
});

export const Chat: React.FC<ChatProps> = ({
  messages,
  input,
  allowPasting = true,
  onInputChange,
  onSend,
  isAgentReplying: isAgentMessageLoading,
  state,
  sources,
  sessionId,
  locale,
  translations: translationOverrides,
  mode = "interactive",
  informationMessage,
  onInformationMessageClose,
  onThumbsFeedback,
  messageFeedback,
  showFeedback = false,
}) => {
  const pickedLocale: Locale = (locale && locale in Translations) ? locale as Locale : "en";
  const baseTranslations = Translations[pickedLocale];
  const t: Translation = {
    ...baseTranslations,
    ...translationOverrides,
  };

  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const [sourcesDrawerOpen, setSourcesDrawerOpen] = React.useState(false);
  const [selectedMessageSources, setSelectedMessageSources] = React.useState<Message | null>(null);
  const [allSourcesDrawerOpen, setAllSourcesDrawerOpen] = React.useState(false);

  const scrollToBottom = React.useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  React.useEffect(() => {
    scrollToBottom();
  }, [messages, isAgentMessageLoading, scrollToBottom]);

  const handleSend = React.useCallback(() => {
    onSend();
    // Small delay to ensure the message is added to the DOM before scrolling
    setTimeout(scrollToBottom, 0);
  }, [onSend, scrollToBottom]);

  const handleOpenSources = React.useCallback((message: Message) => {
    setSelectedMessageSources(message);
    setSourcesDrawerOpen(true);
    setAllSourcesDrawerOpen(false);
  }, []);

  const handleCloseDrawer = React.useCallback(() => {
    setSourcesDrawerOpen(false);
  }, []);

  const handleOpenAllSources = React.useCallback(() => {
    setAllSourcesDrawerOpen(true);
    setSourcesDrawerOpen(false);
  }, []);

  const handleCloseAllSources = React.useCallback(() => {
    setAllSourcesDrawerOpen(false);
  }, []);

  // Handle Escape key to close sources panel
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (sourcesDrawerOpen) {
          handleCloseDrawer();
        } else if (allSourcesDrawerOpen) {
          handleCloseAllSources();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [sourcesDrawerOpen, allSourcesDrawerOpen, handleCloseDrawer, handleCloseAllSources]);

  return (
    <TranslationsContext.Provider value={t}>
      <Root>
        <ChatContainer>
          {mode === "interactive" && (sessionId || (sources && sources.length > 0)) && (
            <ChatHeader>
              <SessionIdContainer>
                {sessionId && (
                  <SessionIdValue>{sessionId}</SessionIdValue>
                )}
              </SessionIdContainer>
              <div>
                {sources && sources.length > 0 && (
                  <Button variant="icon" onClick={handleOpenAllSources} title={t.TOOLTIP_ALL_SOURCES}>
                    <Icon.Notes />
                  </Button>
                )}
              </div>
            </ChatHeader>
          )}

          <MessagesContainer ref={messagesContainerRef}>
            {messages.length === 0 && !isAgentMessageLoading && state === "READY" ? (
              <EmptyStateContainer>
                <EmptyStateContent>
                  <FadeUpAnimation>
                    <Illustration.PsychologistAndPatient width={456} />
                  </FadeUpAnimation>
                  <div style={{ marginLeft: -116 }}>
                    <FadeUpAnimation delay={320}>
                      <Typography variant='h1' color="BLACK_GREY">{t.GREETING_TITLE}</Typography>
                    </FadeUpAnimation>
                    <FadeUpAnimation delay={460}>
                      <Typography style={{ maxWidth: 356 }} variant='body' color="BLACK_GREY">{t.GREETING_MESSAGE}</Typography>
                    </FadeUpAnimation>
                  </div>
                </EmptyStateContent>
              </EmptyStateContainer>
            ) : null}
            <StreamContainer>
              {state === "INITIATING" ? (
                <Spinner />
              ) : null}
              {messages.map((m) => (
                <MessageRow
                  key={m.id}
                  message={m}
                  onOpenSources={() => handleOpenSources(m)}
                  mode={mode}
                  onThumbsFeedback={onThumbsFeedback}
                  messageFeedback={messageFeedback}
                  showFeedback={showFeedback}
                />
              ))}
              {isAgentMessageLoading ? (
                <Thinking />
              ) : null}
            </StreamContainer>
          </MessagesContainer>

          {mode === "interactive" && (
            <InputWrapper>
              <InputContainer>
                <MessageInput
                  value={input}
                  placeholder={t.INPUT_PLACEHOLDER}
                  disabled={state === "INITIATING" || state === "DISABLED"}
                  allowPasting={allowPasting}
                  onChange={onInputChange}
                  onSend={handleSend}
                  informationMessage={informationMessage}
                  onInformationMessageClose={onInformationMessageClose}
                />
              </InputContainer>
            </InputWrapper>
          )}
        </ChatContainer>

        <RightSpacer $isExpanded={sourcesDrawerOpen || allSourcesDrawerOpen}>
          {sourcesDrawerOpen && selectedMessageSources && (
            <SourcesPanel
              message={selectedMessageSources}
              sources={sources}
              onClose={handleCloseDrawer}
            />
          )}
          {allSourcesDrawerOpen && sources && (
            <AllSourcesPanel
              sources={sources}
              onClose={handleCloseAllSources}
            />
          )}
        </RightSpacer>
      </Root>
    </TranslationsContext.Provider >
  );
};

const MessageRow: React.FC<{
  message: Message;
  sources?: ExternalSources;
  onOpenSources?: () => void;
  mode?: "interactive" | "readonly";
  onThumbsFeedback?: (messageId: string, feedback: boolean | undefined) => void;
  messageFeedback?: Record<string, boolean | undefined>;
  showFeedback?: boolean;
}> = ({ message, onOpenSources, mode = "interactive", onThumbsFeedback, messageFeedback, showFeedback }) => {
  const t = useTranslation();

  if (message.author === "model") {
    return (
      <AssistantMessageRow>
        <Avatar>
          <Icon.AutomaticSent />
        </Avatar>
        <MessageBubble role="model">
          {message.error ? (
            <Typography variant="body" color="RED">
              {message.text}
            </Typography>
          ) : (
            <AssistantContent
              message={message}
              onOpenSources={onOpenSources}
              mode={mode}
              onThumbsFeedback={onThumbsFeedback}
              messageFeedback={messageFeedback}
              showFeedback={showFeedback}
            />
          )}
        </MessageBubble>
      </AssistantMessageRow>
    );
  }

  const isUser = message.author === 'user';
  const isSystem = message.author === 'system';
  return (
    <StyledMessageRow $isUser={isUser}>
      <MessageBubble
        role={isSystem ? 'system' : isUser ? 'user' : 'model'}
      >
        {message.error ? (
          <Typography variant="body" color="RED">
            {t.ERROR_PREFIX}: {message.text}
          </Typography>
        ) : (
          <PlainText text={message.text} />
        )}
      </MessageBubble>
    </StyledMessageRow>
  );
};

const AssistantContent: React.FC<{
  message: Message;
  onOpenSources?: () => void;
  mode?: "interactive" | "readonly";
  onThumbsFeedback?: (messageId: string, feedback: boolean | undefined) => void;
  messageFeedback?: Record<string, boolean | undefined>;
  showFeedback?: boolean;
}> = ({ message, onOpenSources, mode = "interactive", onThumbsFeedback, messageFeedback, showFeedback }) => {
  const t = useTranslation();
  const text = message.text ?? '';
  const candidates = message.candidates ?? [];
  const [copied, setCopied] = React.useState(false);

  // Get feedback state from prop
  const currentFeedback = messageFeedback?.[message.id];
  const thumbsUpActive = currentFeedback === true;
  const thumbsDownActive = currentFeedback === false;

  // Pick the first candidate to check for grounding metadata
  const firstCandidate = candidates?.[0];
  const groundingMetadata = firstCandidate?.groundingMetadata;
  const hasGroundingChunks = groundingMetadata?.groundingSupports && groundingMetadata.groundingSupports.length > 0;

  const handleCopyText = React.useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  const handleThumbsUp = React.useCallback(() => {
    if (!onThumbsFeedback) return;
    // If already thumbs up, clear it; otherwise set to thumbs up
    const newFeedback = thumbsUpActive ? undefined : true;
    onThumbsFeedback(message.id, newFeedback);
  }, [thumbsUpActive, message.id, onThumbsFeedback]);

  const handleThumbsDown = React.useCallback(() => {
    if (!onThumbsFeedback) return;
    // If already thumbs down, clear it; otherwise set to thumbs down
    const newFeedback = thumbsDownActive ? undefined : false;
    onThumbsFeedback(message.id, newFeedback);
  }, [thumbsDownActive, message.id, onThumbsFeedback]);

  return (
    <>
      <ReactMarkdown>{text}</ReactMarkdown>
      {mode === "interactive" && (
        <MessageActions>
          <Button
            variant="icon"
            onClick={handleCopyText}
            disabled={copied}
            title={t.TOOLTIP_COPY}
          >
            <Icon.Copy style={{ width: 16 }} />
          </Button>
          {hasGroundingChunks && (
            <Button
              variant="icon"
              onClick={onOpenSources}
              title={t.TOOLTIP_SOURCES}
            >
              <Icon.Info style={{ width: 16 }} />
            </Button>
          )}
          {showFeedback && (!thumbsDownActive) && (
            <Button
              variant="icon"
              onClick={handleThumbsUp}
              title={t.TOOLTIP_THUMBS_UP}
            >
              <Icon.ThumbsUp
                style={{
                  width: 16,
                  color: thumbsUpActive ? defaultTheme.COLORS.PRIMARY : undefined,
                }}
              />
            </Button>
          )}
          {showFeedback && (!thumbsUpActive) && (
            <Button
              variant="icon"
              onClick={handleThumbsDown}
              title={t.TOOLTIP_THUMBS_DOWN}
            >
              <Icon.ThumbsDown
                style={{
                  width: 16,
                  color: thumbsDownActive
                    ? defaultTheme.COLORS.PRIMARY
                    : undefined,
                }}
              />
            </Button>
          )}
        </MessageActions>
      )}
      {mode === "readonly" && (
        <MessageActions>
          {showFeedback && thumbsUpActive && (
            <Button variant="icon" title={t.TOOLTIP_THUMBS_UP}>
              <Icon.ThumbsUp
                style={{
                  width: 16,
                  color: defaultTheme.COLORS.PRIMARY,
                }}
              />
            </Button>
          )}
          {showFeedback && thumbsDownActive && (
            <Button variant="icon" title={t.TOOLTIP_THUMBS_DOWN}>
              <Icon.ThumbsDown
                style={{
                  width: 16,
                  color: defaultTheme.COLORS.PRIMARY
                }}
              />
            </Button>
          )}
        </MessageActions>
      )}
    </>
  );
};

const MessageActions = styled.div({
  marginTop: defaultTheme.SPACING.SM,
  display: 'flex',
  gap: defaultTheme.SPACING.XS,
  justifyContent: 'flex-start',
});

const ActionButton = styled.button<{ $copied?: boolean }>(({ $copied }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: defaultTheme.SPACING.XS,
  padding: `${defaultTheme.SPACING.XS} ${defaultTheme.SPACING.SM}`,
  fontSize: defaultTheme.FONT.SIZE.SMALL,
  color: $copied ? defaultTheme.COLORS.BLUE : defaultTheme.COLORS.GRAY,
  background: $copied ? defaultTheme.COLORS.WARM_LIGHT : 'transparent',
  border: `1px solid ${$copied ? defaultTheme.COLORS.BLUE : defaultTheme.COLORS.GRAY_MEDIUM}`,
  borderRadius: defaultTheme.BORDER_RADIUS.SM,
  cursor: $copied ? 'default' : 'pointer',
  transition: 'all 0.2s ease',

  '&:hover': {
    background: $copied ? defaultTheme.COLORS.WARM_LIGHT : defaultTheme.COLORS.GRAY_LIGHT,
    borderColor: $copied ? defaultTheme.COLORS.BLUE : defaultTheme.COLORS.GRAY,
    color: $copied ? defaultTheme.COLORS.BLUE : defaultTheme.COLORS.GRAY_BLACK,
  },

  '&:disabled': {
    cursor: 'default',
  },

  '& svg': {
    width: '14px',
    height: '14px',
  },
}));

const PlainText: React.FC<{ text: string; inline?: boolean }> = ({ text, inline }) => {
  if (inline) {
    return (
      <span>
        <ReactMarkdown
          components={{
            p: ({ children }) => <span>{children}</span>,
          }}
        >
          {text || ''}
        </ReactMarkdown>
      </span>
    );
  }
  return (
    <ReactMarkdown>{text || ''}</ReactMarkdown>
  );
};
